import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { serviceCategories } from '../mock';

const Header = ({ onOpenPopup, isLandingPage = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [activeCategory, setActiveCategory] = useState(0);

  const handleMouseEnter = (category) => {
    setOpenDropdown(category);
  };

  const handleMouseLeave = () => {
    setOpenDropdown(null);
    setActiveCategory(0); // Reset to first category
  };

  const handleCategoryHover = (index) => {
    setActiveCategory(index);
  };

  if (isLandingPage) {
    return (
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/lp" className="flex items-center">
              <img src="/assets/img/brand/logo-dark.png" alt="Trusted Trademark Filer" className="h-12" />
            </Link>
            <div className="flex items-center gap-4">
              <button
                onClick={onOpenPopup}
                className="bg-[#FF4A2A] text-white px-6 py-2.5 rounded-lg font-medium hover:bg-[#E63E1F] transition-colors duration-300"
              >
                Register My Trademark
              </button>
              <button
                onClick={() => window.openChatWidget && window.openChatWidget()}
                className="border-2 border-[#FF4A2A] text-[#FF4A2A] px-6 py-2.5 rounded-lg font-medium hover:bg-[#FF4A2A] hover:text-white transition-all duration-300"
              >
                Chat Now
              </button>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="bg-white shadow-md sticky top-0 z-50 border-b-2 border-[#E2F8FE]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <Link to="/" className="flex items-center group">
            <img src="/assets/img/brand/logo-dark.png" alt="Trusted Trademark Filer" className="h-12" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            <Link to="/" className="relative text-gray-700 hover:text-[#FF4A2A] transition-colors duration-300 font-medium group">
              Home
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#FF4A2A] transition-all duration-300 group-hover:w-full"></span>
            </Link>
            <Link to="/about" className="relative text-gray-700 hover:text-[#FF4A2A] transition-colors duration-300 font-medium group">
              About Us
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#FF4A2A] transition-all duration-300 group-hover:w-full"></span>
            </Link>
            
            {/* Services Mega Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => handleMouseEnter('services')}
              onMouseLeave={handleMouseLeave}
            >
              <button className="flex items-center text-gray-700 hover:text-[#FF4A2A] transition-colors duration-300 font-medium">
                Services <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              {openDropdown === 'services' && (
                <div 
                  className="absolute top-full left-1/2 transform -translate-x-1/2 mt-0 w-screen max-w-7xl bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden"
                  onMouseEnter={() => handleMouseEnter('services')}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="grid grid-cols-12 gap-0">
                    {/* Left Sidebar - Categories */}
                    <div className="col-span-3 bg-gray-50 p-6 border-r border-gray-200 h-[380px] rounded-l-lg">
                      <div className="space-y-1">
                        {serviceCategories.map((category, index) => (
                          <div 
                            key={category.id}
                            onMouseEnter={() => handleCategoryHover(index)}
                            className={`text-sm font-semibold py-2 cursor-pointer transition-colors ${
                              activeCategory === index 
                                ? 'text-[#FF4A2A]' 
                                : 'text-gray-700 hover:text-[#FF4A2A]'
                            }`}
                          >
                            {category.name}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Middle - Services in 2 columns */}
                    <div className="col-span-6 p-6 h-[380px] overflow-y-auto">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        {serviceCategories[activeCategory].items.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className="group"
                            onClick={() => setOpenDropdown(null)}
                          >
                            <div className="flex items-start gap-2">
                              <span className="text-gray-400 mt-1">▸</span>
                              <div>
                                <div className="text-sm font-semibold text-gray-900 group-hover:text-[#FF4A2A] transition-colors">
                                  {item.name}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {item.description}
                                </div>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>

                    {/* Right - Promotional Section */}
                    <div className="col-span-3 bg-gradient-to-br from-[#FF4A2A] to-[#E63E1F] h-[380px] rounded-r-lg flex items-center justify-center">
                      <div className="text-center space-y-4 px-6 py-6">
                        <div className="w-24 h-24 mx-auto bg-white bg-opacity-20 backdrop-blur-sm rounded-full flex items-center justify-center shadow-xl border-4 border-white border-opacity-30">
                          <svg className="w-16 h-16" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="50" cy="35" r="15" fill="white"/>
                            <path d="M30 60C30 51.7 37.7 45 46 45H54C62.3 45 70 51.7 70 60V75H30V60Z" fill="white"/>
                            <path d="M35 45L30 50L35 55" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M65 45L70 50L65 55" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="space-y-2">
                          <p className="text-base font-bold text-white">Expert Legal Support</p>
                          <p className="text-xs text-white opacity-90">Professional attorneys guiding you every step</p>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-center gap-2 text-white text-xs">
                            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            <span>Free Consultation</span>
                          </div>
                          <div className="flex items-center justify-center gap-2 text-white text-xs">
                            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            <span>24/7 Support</span>
                          </div>
                          <div className="flex items-center justify-center gap-2 text-white text-xs">
                            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            <span>95% Success Rate</span>
                          </div>
                        </div>
                        <button 
                          onClick={onOpenPopup}
                          className="bg-white text-[#FF4A2A] px-4 py-2.5 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-lg w-full text-sm flex items-center justify-center gap-2"
                        >
                          Get Started Now
                          <span className="text-lg">→</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link to="/contact" className="relative text-gray-700 hover:text-[#FF4A2A] transition-colors duration-300 font-medium group">
              Contact Us
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#FF4A2A] transition-all duration-300 group-hover:w-full"></span>
            </Link>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={onOpenPopup}
              className="bg-[#FF4A2A] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#E63E1F] transition-all duration-300 hover:scale-105 shadow-md"
            >
              Get Started
            </button>
            <button
              onClick={() => window.openChatWidget && window.openChatWidget()}
              className="border-2 border-[#FF4A2A] text-[#FF4A2A] px-6 py-3 rounded-xl font-semibold hover:bg-[#FF4A2A] hover:text-white transition-all duration-300 hover:scale-105"
            >
              Chat Now
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100">
            <nav className="flex flex-col space-y-4">
              <Link to="/" className="text-gray-700 hover:text-[#FF4A2A] transition-colors px-4 py-2" onClick={() => setIsMenuOpen(false)}>
                Home
              </Link>
              <Link to="/about" className="text-gray-700 hover:text-[#FF4A2A] transition-colors px-4 py-2" onClick={() => setIsMenuOpen(false)}>
                About Us
              </Link>
              
              {/* Services Accordion */}
              <div>
                <button
                  onClick={() => setOpenDropdown(openDropdown === 'services' ? null : 'services')}
                  className="flex items-center justify-between w-full text-gray-700 hover:text-[#FF4A2A] transition-colors px-4 py-2"
                >
                  <span>Services</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${openDropdown === 'services' ? 'rotate-180' : ''}`} />
                </button>
                
                {openDropdown === 'services' && (
                  <div className="mt-2 ml-4 space-y-2 max-h-96 overflow-y-auto">
                    {serviceCategories.map((category) => (
                      <div key={category.id} className="mb-4">
                        <div className="text-sm font-bold text-[#FF4A2A] mb-2 px-4">
                          {category.name}
                        </div>
                        <div className="space-y-2">
                          {category.items.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              className="block text-sm text-gray-600 hover:text-[#FF4A2A] transition-colors px-4 py-2 hover:bg-gray-50"
                              onClick={() => {
                                setIsMenuOpen(false);
                                setOpenDropdown(null);
                              }}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <Link to="/contact" className="text-gray-700 hover:text-[#FF4A2A] transition-colors px-4 py-2" onClick={() => setIsMenuOpen(false)}>
                Contact Us
              </Link>
              
              <div className="px-4 space-y-3 pt-2">
                <button
                  onClick={() => {
                    onOpenPopup();
                    setIsMenuOpen(false);
                  }}
                  className="bg-[#FF4A2A] text-white px-6 py-2.5 rounded-lg font-medium w-full"
                >
                  Get Started
                </button>
                <button
                  onClick={() => {
                    window.openChatWidget && window.openChatWidget();
                    setIsMenuOpen(false);
                  }}
                  className="border-2 border-[#FF4A2A] text-[#FF4A2A] px-6 py-2.5 rounded-lg font-medium w-full"
                >
                  Chat Now
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;